import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useMediaQuery } from 'react-responsive';

import { logout } from 'data_system/token_auth/actions';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { ROOT } from 'ui_system/token_auth/routes';

import { Theme } from 'ui_system/theme';

import { getUser } from 'data_system/token_auth/selectors';
import { ThreeBars } from 'views/ui/icons/anyfont-icons';
import { SETTINGS, ORDER_HISTORY } from '../routes/routes';

const navHeight = '50px';

const NavBarStyled = styled.nav`
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: ${navHeight};
  width: 100%;
  background: ${Theme.colors.black};

  @media (max-width: ${Theme.mobile.phoneWidth}px) {
    display: block;
  }
`;

const MenuItem = styled.li`
  padding: 15px 30px 15px 15px;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${Theme.colors.line};
  color: ${Theme.colors.lightestText};
  cursor: pointer;
`;

const Menu = styled.ul`
  position: absolute;
  display: ${(props) => (props.openMenu ? 'flex' : 'none')};
  flex-direction: column;
  border-radius: 5px;
  top: ${navHeight};
  right: 25px;
  z-index: 1;
  color: ${Theme.colors.lightestText};
  background: ${Theme.colors.darkBackground};

  & > ${MenuItem}:last-child {
    border-bottom: 0px;
  }

  @media (max-width: ${Theme.mobile.phoneWidth}px) {
    right: 15px;
  }
`;

const NavMenuLink = styled.div`
  padding-left: 25px;
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  @media (max-width: ${Theme.mobile.phoneWidth}px) {
    justify-content: space-between;
    padding: 0 16px 0 24px;
  }
`;

const NavBarName = styled.h1`
  font-size: 17px;
  color: ${Theme.colors.lightestText};
`;

const DownArrow = styled.i`
  border: solid white;
  border-width: 0 2px 2px 0;
  display: inline-block;
  padding: 5px;
  margin: 0px 35px 5px 15px;

  transform: rotate(45deg);
`;

const Triangle = styled.div`
  width: 0;
  height: 0;
  position: absolute;
  top: -10px;
  right: 7px;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 10px solid #555;
  @media (max-width: ${Theme.mobile.phoneWidth}px) {
    right: 4px;
  }
`;

export const NavBar = () => {
  const [openMenu, setOpenMenu] = useState(false);
  const { name } = useSelector(getUser);
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: Theme.mobile.phoneWidth - 1 });

  // Add event listener to document to close the menu on click
  useEffect(() => {
    let listenerAdded = false;
    function closeMenu() {
      setOpenMenu(false);
      document.removeEventListener('click', closeMenu);
      listenerAdded = false;
    }

    if (openMenu) {
      document.addEventListener('click', closeMenu);
      listenerAdded = true;
    }

    if (listenerAdded) {
      return () => document.removeEventListener('click', closeMenu);
    }
    return undefined;
  }, [openMenu]);

  const handleMenuToggle = (e) => {
    // Prevents document listener from getting triggered
    // See: https://stackoverflow.com/a/24421834
    e.nativeEvent.stopImmediatePropagation();
    setOpenMenu(!openMenu);
  };

  return (
    <NavBarStyled>
      <NavMenuLink onClick={!isMobile && handleMenuToggle}>
        <NavBarName data-testid="affiliate-portal-nav-bar">{name}</NavBarName>
        {isMobile ? (
          <ThreeBars
            data-testid="dropdown-hamburger"
            fill="white"
            height="24px"
            width="24px"
            onClick={handleMenuToggle}
          />
        ) : (
          <DownArrow />
        )}
      </NavMenuLink>
      <Menu openMenu={openMenu}>
        <Triangle />
        <Link to={ROOT}>
          <MenuItem data-testid="menu-item-experiences">Experiences</MenuItem>
        </Link>
        <Link to={SETTINGS}>
          <MenuItem data-testid="menu-item-account-settings">
            Account Settings
          </MenuItem>
        </Link>
        <Link to={ORDER_HISTORY}>
          <MenuItem data-testid="menu-item-order-history">
            Order History
          </MenuItem>
        </Link>
        <MenuItem
          onClick={() => dispatch(logout)}
          data-testid="menu-item-signout"
        >
          Sign Out
        </MenuItem>
      </Menu>
    </NavBarStyled>
  );
};
